import React, { lazy, Suspense } from "react";
import { Outlet, useMatches } from "react-router-dom";
import { LayoutSlotProvider } from "./LayoutSlotContext";
import type { LayoutType } from "routes/types";

// Lazy load layout wrappers for code-splitting
const FullscreenLayout = lazy(() => import("./layouts/FullscreenLayout"));
const MainOverviewLayout = lazy(() => import("./layouts/MainOverviewLayout"));
const DetailLayout = lazy(() => import("./layouts/DetailLayout"));
const PreviewPaneLayout = lazy(() => import("./layouts/PreviewPaneLayout"));

/**
 * Replaces the bare <Outlet /> in MainLayout.
 *
 * Reads the `layout` field from matched route handles, walking from the deepest
 * matched route upward (most specific wins). Wraps <Outlet /> in the appropriate
 * layout template. Falls back to a plain <Outlet /> when no layout is declared,
 * ensuring full backward compatibility with all existing routes.
 *
 * All layout variants share a LayoutSlotProvider so page components can inject
 * named slots (returnSlot, subheaderSlot, toolbarSlot, …) via useRegisterLayoutSlot.
 */
const LayoutOutlet: React.FC = () => {
  const matches = useMatches();

  // Walk from deepest to shallowest — the most specific route wins
  const activeLayout = [...matches]
    .reverse()
    .map((m) => (m.handle as { layout?: LayoutType } | undefined)?.layout)
    .find((l) => l !== undefined && l !== "default") as LayoutType | undefined;

  const renderLayout = () => {
    switch (activeLayout) {
      case "fullscreen":
        return <FullscreenLayout />;
      case "mainOverview":
        return <MainOverviewLayout />;
      case "detail":
        return <DetailLayout />;
      case "previewPane":
        return <PreviewPaneLayout />;
      default:
        return <Outlet />;
    }
  };

  return (
    <LayoutSlotProvider>
      <Suspense fallback={null}>{renderLayout()}</Suspense>
    </LayoutSlotProvider>
  );
};

export default LayoutOutlet;
